import * as L from 'monocle-ts/Lens'
import * as RA from 'fp-ts/ReadonlyArray'
import * as RR from 'fp-ts/ReadonlyRecord'
import { pipe } from 'fp-ts/function'
import * as E from 'fp-ts/Either'
import { uuid, UUID } from '@woorcs/types/UUID'
import * as S from '@woorcs/types/Schemable'
import {
  I18nConfig,
  FormDocument,
  TranslateableText,
  ResponseSet
} from '@woorcs/form'
import * as ElementTree from '@woorcs/types/ElementTree'
import { DecodeError } from 'io-ts/lib/Decoder'

import {
  InspectionFormDocument,
  InspectionFormPageElement
} from './InspectionFormDocument'

// -------------------------------------------------------------------------------------
// model
// -------------------------------------------------------------------------------------

type Documents = Record<string, FormDocument.FormDocument>

type ResponseSets = Record<string, ResponseSet.ResponseSet>

export interface InspectionFormDefinition extends InspectionFormDocument {
  readonly description: TranslateableText.TranslateableText
  readonly i18n: I18nConfig.I18nConfig
  readonly documents: Documents
  readonly responseSets: ResponseSets
}

export const ResponseSets: S.Type<ResponseSets> = S.type((S) =>
  S.record(ResponseSet.ResponseSet.schema(S))
)

export const Documents: S.Type<Documents> = S.type((S) =>
  S.record(FormDocument.schema(S))
)

export const InspectionFormDefinition: S.Type<InspectionFormDefinition> =
  S.type((S) =>
    S.struct({
      type: S.literal('InspectionForm'),
      id: UUID.schema(S),
      title: TranslateableText.TranslateableText.schema(S),
      description: TranslateableText.TranslateableText.schema(S),
      documents: Documents.schema(S),
      responseSets: ResponseSets.schema(S),
      i18n: I18nConfig.schema(S),
      children: S.array(InspectionFormPageElement.schema(S))
    })
  )

// -------------------------------------------------------------------------------------
// constructors
// -------------------------------------------------------------------------------------

export const inspectionFormDefinition = (
  definition: Pick<InspectionFormDefinition, 'title' | 'i18n'> &
    Partial<Omit<InspectionFormDefinition, 'title' | 'i18n'>>
): InspectionFormDefinition => ({
  type: 'InspectionForm',
  id: uuid(),
  children: [],
  description: TranslateableText.translateableText(''),
  documents: {},
  responseSets: ResponseSet.createDefaultResponseSets().reduce(
    (responseSets, responseSet) => ({
      ...responseSets,
      [responseSet.id]: responseSet
    }),
    {}
  ),
  ...definition
})

// -------------------------------------------------------------------------------------
// lenses
// -------------------------------------------------------------------------------------

export const lens = L.id<InspectionFormDefinition>()

export const documentsLens = pipe(lens, L.prop('documents'))

export const responseSetsLens = pipe(lens, L.prop('responseSets'))

// -------------------------------------------------------------------------------------
// documents
// -------------------------------------------------------------------------------------

export const getFormDocument = (id: UUID) =>
  pipe(documentsLens, L.key(id)).getOption

export const addFormDocument = (document: FormDocument.FormDocument) =>
  pipe(documentsLens, L.modify(RR.upsertAt(document.id, document)))

export const removeFormDocument = (document: FormDocument.FormDocument) =>
  pipe(documentsLens, L.modify(RR.deleteAt(document.id)))

export const updateFormDocument = addFormDocument

// -------------------------------------------------------------------------------------
// response sets
// -------------------------------------------------------------------------------------

export const getResponseSet = (id: UUID) =>
  pipe(responseSetsLens, L.key(id)).getOption

export const addResponseSet = (responseSet: ResponseSet.ResponseSet) =>
  pipe(responseSetsLens, L.modify(RR.upsertAt(responseSet.id, responseSet)))

export const removeResponseSet = (responseSet: ResponseSet.ResponseSet) =>
  pipe(responseSetsLens, L.modify(RR.deleteAt(responseSet.id)))

export const updateResponseSet = addResponseSet

// -------------------------------------------------------------------------------------
// validation
// -------------------------------------------------------------------------------------

type InspectionFormValidationError =
  | {
      _tag: 'DecodeError'
      error: DecodeError
    }
  | {
      _tag: 'UniqueElementIdError'
      id: UUID
      count: number
    }
  | {
      _tag: 'UniqueTranslationKeysError'
      translationKey: UUID
      paths: string[]
      count: number
    }

const validateUniqueElementIds = (definition: InspectionFormDefinition) => {
  return pipe(
    definition,
    ElementTree.toArray,
    RA.map((element) => element.id),
    RA.reduce({} as RR.ReadonlyRecord<string, number>, (acc, id) => {
      const current = acc[id]

      if (current) {
        return {
          ...acc,
          [id]: current + 1
        }
      }

      return {
        ...acc,
        [id]: 1
      }
    }),
    RR.toEntries,
    RA.filter(([_, count]) => count > 1),
    E.fromPredicate(RA.isEmpty, (ids) =>
      ids.map(
        ([id, count]) =>
          ({
            _tag: 'UniqueElementIdError',
            id,
            count
          }) as InspectionFormValidationError
      )
    ),
    E.map(() => definition)
  )
}

const textKeys = [
  'title',
  'description',
  'placeholder',
  'informativeText'
] as const

type TranslateableElement = Partial<{
  [key in (typeof textKeys)[number]]: TranslateableText.TranslateableText
}>

const validateUniqueTranslationKeys = (
  definition: InspectionFormDefinition
) => {
  return pipe(
    definition,
    ElementTree.toArray,
    RA.reduce({} as RR.ReadonlyRecord<string, string[]>, (acc, element) => {
      const e: TranslateableElement = element

      return pipe(
        textKeys,
        RA.reduce(acc, (acc, key) => {
          const text = e[key]

          if (!text) {
            return acc
          }

          if (acc[text.id]) {
            return {
              ...acc,
              [text.id]: [...acc[text.id], `${element.id}:${key}`]
            }
          }

          return {
            ...acc,
            [text.id]: [`${element.id}:${key}`]
          }
        })
      )
    }),
    RR.toEntries,
    RA.filter(([, paths]) => paths.length > 1),
    E.fromPredicate(RA.isEmpty, (ids) =>
      ids.map(
        ([translationKey, paths]) =>
          ({
            _tag: 'UniqueTranslationKeysError',
            translationKey,
            paths,
            count: paths.length
          }) as InspectionFormValidationError
      )
    ),
    E.map(() => definition)
  )
}

export const validate = (
  definition: InspectionFormDefinition
): E.Either<InspectionFormValidationError[], InspectionFormDefinition> => {
  return pipe(
    InspectionFormDefinition.decode(definition),
    E.mapLeft((error) => [
      {
        _tag: 'DecodeError',
        error
      } as InspectionFormValidationError
    ]),
    E.chain(validateUniqueElementIds),
    E.chain(validateUniqueTranslationKeys)
  )
}
